import React, { useState, useEffect } from 'react';
import Icon from './AppIcon';
import { checkSupabaseHealth, isSupabaseError, getErrorMessage } from '../utils/supabaseHealthCheck';

const ErrorFallback = ({ error, resetError, errorInfo }) => {
  const [isCheckingConnection, setIsCheckingConnection] = useState(false);
  const [connectionStatus, setConnectionStatus] = useState(null);
  const [errorType, setErrorType] = useState('generic');

  useEffect(() => {
    // Determine error type
    if (isSupabaseError(error)) {
      setErrorType('supabase');
      // Auto-check connection for Supabase errors
      handleCheckConnection();
    } else {
      setErrorType('generic');
    }
  }, [error]);

  const handleCheckConnection = async () => {
    setIsCheckingConnection(true);
    const health = await checkSupabaseHealth();
    setConnectionStatus(health);
    setIsCheckingConnection(false);
  };

  const handleRetry = () => {
    setConnectionStatus(null);
    if (resetError) {
      resetError();
    } else {
      window.location?.reload();
    }
  };

  const handleGoHome = () => {
    window.location.href = '/';
  };

  const userFriendlyMessage = getErrorMessage(error);

  return (
    <div className="min-h-screen flex items-center justify-center bg-neutral-50 p-4">
      <div className="text-center p-8 max-w-lg w-full bg-white rounded-lg shadow-sm">
        {/* Error Icon */}
        <div className="flex justify-center items-center mb-4">
          {errorType === 'supabase' ? (
            <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center">
              <Icon name="WifiOff" size={32} color="#ef4444" />
            </div>
          ) : (
            <svg xmlns="http://www.w3.org/2000/svg" width="64px" height="64px" viewBox="0 0 32 33" fill="none">
              <path d="M16 28.5C22.6274 28.5 28 23.1274 28 16.5C28 9.87258 22.6274 4.5 16 4.5C9.37258 4.5 4 9.87258 4 16.5C4 23.1274 9.37258 28.5 16 28.5Z" stroke="#343330" strokeWidth="2" strokeMiterlimit="10" />
              <path d="M11.5 15.5C12.3284 15.5 13 14.8284 13 14C13 13.1716 12.3284 12.5 11.5 12.5C10.6716 12.5 10 13.1716 10 14C10 14.8284 10.6716 15.5 11.5 15.5Z" fill="#343330" />
              <path d="M20.5 15.5C21.3284 15.5 22 14.8284 22 14C22 13.1716 21.3284 12.5 20.5 12.5C19.6716 12.5 19 13.1716 19 14C19 14.8284 19.6716 15.5 20.5 15.5Z" fill="#343330" />
              <path d="M21 22.5C19.9625 20.7062 18.2213 19.5 16 19.5C13.7787 19.5 12.0375 20.7062 11 22.5" stroke="#343330" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          )}
        </div>

        {/* Error Title */}
        <h1 className="text-2xl font-semibold text-neutral-800 mb-2">
          {errorType === 'supabase' ? 'Connection Error' : 'Something went wrong'}
        </h1>

        {/* User-Friendly Error Message */}
        <p className="text-neutral-600 text-base mb-4">
          {userFriendlyMessage}
        </p>

        {/* Connection Status */}
        {errorType === 'supabase' && connectionStatus && (
          <div className={`mb-4 p-3 rounded-lg text-sm ${
            connectionStatus?.isHealthy 
              ? 'bg-green-50 text-green-800 border border-green-200' :'bg-red-50 text-red-800 border border-red-200'
          }`}>
            <div className="flex items-center justify-center gap-2">
              <Icon 
                name={connectionStatus?.isHealthy ? 'CheckCircle' : 'AlertCircle'} 
                size={16} 
                color={connectionStatus?.isHealthy ? '#16a34a' : '#dc2626'}
              />
              <span>
                {connectionStatus?.isHealthy 
                  ? 'Connection restored' 
                  : connectionStatus?.error}
              </span>
            </div>
          </div>
        )}

        {/* Action Buttons */}
        <div className="flex flex-col sm:flex-row gap-3 justify-center items-center mt-6">
          <button
            onClick={handleRetry}
            disabled={isCheckingConnection}
            className="bg-blue-500 hover:bg-blue-600 disabled:bg-blue-300 text-white font-medium py-2.5 px-6 rounded-lg flex items-center gap-2 transition-colors duration-200 shadow-sm w-full sm:w-auto"
          >
            <Icon name="RefreshCw" size={18} color="#fff" />
            {isCheckingConnection ? 'Checking...' : 'Try Again'}
          </button>

          <button
            onClick={handleGoHome}
            className="bg-neutral-100 hover:bg-neutral-200 text-neutral-700 font-medium py-2.5 px-6 rounded-lg flex items-center gap-2 transition-colors duration-200 w-full sm:w-auto"
          >
            <Icon name="Home" size={18} color="#404040" />
            Go Home
          </button>
        </div>

        {/* Technical Details (Collapsible) */}
        {process.env.NODE_ENV === 'development' && error && (
          <details className="mt-6 text-left">
            <summary className="cursor-pointer text-sm text-neutral-500 hover:text-neutral-700 font-medium">
              Technical Details (Dev Only)
            </summary>
            <div className="mt-2 p-3 bg-neutral-100 rounded text-xs font-mono text-neutral-700 overflow-auto max-h-40">
              <p className="font-semibold mb-1">Error:</p>
              <p className="mb-2">{error?.toString()}</p>
              {error?.stack && (
                <>
                  <p className="font-semibold mb-1">Stack:</p>
                  <pre className="whitespace-pre-wrap">{error?.stack}</pre>
                </>
              )}
            </div>
          </details>
        )}
      </div>
    </div>
  );
};

export default ErrorFallback;